import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** One moving selection surface; scrolling stays inside this tab strip. */
export function SlidingTabs<T extends string>({ value, onChange, items, label }: { value: T; onChange: (value: T) => void; items: { id: T; label: ReactNode }[]; label: string }) {
  const root = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const strip = root.current;
    if (!strip) return;
    const measure = () => {
      const button = strip.querySelector<HTMLButtonElement>('[aria-selected="true"]');
      if (!button) return;
      setIndicator(previous => previous.left === button.offsetLeft && previous.width === button.offsetWidth ? previous : { left: button.offsetLeft, width: button.offsetWidth });
      const left = button.offsetLeft, right = left + button.offsetWidth;
      const target = left < strip.scrollLeft ? left : right > strip.scrollLeft + strip.clientWidth ? right - strip.clientWidth : strip.scrollLeft;
      strip.scrollTo?.({ left: target, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    Array.from(strip.children).forEach(child => observer.observe(child));
    return () => observer.disconnect();
  }, [value, items.length]);
  return <div ref={root} role="tablist" aria-label={label} className="mvp-sliding-tabs">
    <span aria-hidden="true" className="mvp-tab-indicator" style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }} />
    {items.map((item, index) => <button key={item.id} type="button" role="tab" aria-selected={value === item.id} tabIndex={value === item.id ? 0 : -1} onClick={() => onChange(item.id)} onKeyDown={event => {
      const target = event.key === 'ArrowRight' ? (index + 1) % items.length : event.key === 'ArrowLeft' ? (index + items.length - 1) % items.length : event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : -1;
      if (target < 0) return;
      event.preventDefault(); onChange(items[target]!.id);
      root.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus({ preventScroll: true });
    }}>{item.label}</button>)}
  </div>;
}
